import { c as _c } from "react/compiler-runtime";
import { createContext, useMemo, use } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { Overwrite } from 'utility-types';
import { makeCommuteSectionsRepository } from 'api/modules/commuteSections';
import { useCommuteSections as useFetchCommuteSections } from '@commutifi-fe/commutifi-specific/Presentation/CommuteSections/hooks';
import { CommuteSectionsRepository } from '@commutifi-fe/commutifi-specific/Domain/Repositories/CommuteSection';
import { CommuteSection } from '@commutifi-fe/commutifi-specific/Domain/Models/CommuteSection';
import { CommutifiErrorData } from '@commutifi-fe/interfaces';
import { CommutifiError } from 'shared/interfaces';
import { useCurrentAccount } from './CurrentAccount';
interface CommuteSectionsProviderProps {
  /**
   * Enable data fetching in the provider. This allows us to use
   * the provider at top level tree without being concerned to fetch
   * too many times the data when we don't need it
   */
  enabled?: boolean;
  children: React.ReactNode;
}
type ICommuteSectionsContext = Overwrite<Partial<UseQueryResult<CommuteSection[], CommutifiError>>, {
  isError: boolean;
}> & {
  commuteSections?: CommuteSection[];
  error: AxiosResponse<CommutifiErrorData, any> | null;
};
export const CommuteSectionsContext = createContext<ICommuteSectionsContext | null>(null);
const commuteSectionsRepository = makeCommuteSectionsRepository();
function CommuteSectionsProvider(t0) {
  const $ = _c(20);
  const {
    enabled,
    children
  } = t0;
  const {
    currentAccount
  } = useCurrentAccount();
  let t1;
  if ($[0] !== currentAccount.id) {
    t1 = {
      accountId: currentAccount.id
    };
    $[0] = currentAccount.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = enabled && !!currentAccount.enterpriseId;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      enabled: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t3) {
    t4 = {
      queryParams: t1,
      queryOptions: t3
    };
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const requestOptions = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = commuteSectionsRepository();
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const fetchCommuteSectionsUseCase = useFetchCommuteSections(t5, requestOptions);
  let t6;
  if ($[8] !== fetchCommuteSectionsUseCase) {
    t6 = fetchCommuteSectionsUseCase.execute();
    $[8] = fetchCommuteSectionsUseCase;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    isLoading,
    isFetching,
    isError,
    isFetched,
    error,
    data: commuteSections
  } = t6;
  let t7;
  if ($[10] !== commuteSections || $[11] !== error || $[12] !== isError || $[13] !== isFetched || $[14] !== isFetching || $[15] !== isLoading) {
    t7 = {
      isLoading,
      isFetching,
      isFetched,
      isError,
      error,
      commuteSections
    };
    $[10] = commuteSections;
    $[11] = error;
    $[12] = isError;
    $[13] = isFetched;
    $[14] = isFetching;
    $[15] = isLoading;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const value = t7;
  if (!enabled) {
    return children;
  }
  let t8;
  if ($[17] !== children || $[18] !== value) {
    t8 = <CommuteSectionsContext value={value}>{children}</CommuteSectionsContext>;
    $[17] = children;
    $[18] = value;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
const useCommuteSectionsContext = () => {
  const context = use(CommuteSectionsContext);
  if (!context) {
    throw new Error('useCommuteSectionsContext must be used within a CommuteSectionsProvider');
  }
  return context;
};
export { CommuteSectionsProvider, useCommuteSectionsContext };
export function makeCommuteSectionsProviderRepository<TQueryParams extends Record<string, any>>(): CommuteSectionsRepository<TQueryParams> {
  return {
    useCommuteSections: () => {
      const {
        isLoading,
        error,
        isFetched,
        commuteSections
      } = useCommuteSectionsContext();
      return {
        isLoading: Boolean(isLoading),
        error,
        isError: Boolean(error),
        isFetching: Boolean(isLoading && !commuteSections),
        isFetched: Boolean(isFetched),
        data: commuteSections
      };
    }
  };
}